<template>
  <div class="physical-assessment-locations">
    <!-- Paper doll outline -->
    <img
      :src="physicalAssessmentImages.anterior.paperDoll.image"
      :style="physicalAssessmentImages.anterior.paperDoll.styles"
      class="iv-image iv-image-body-background"
      :class="{ disabled: readonly }"
      alt="Anterior"
    >
    <!-- Paper doll parts -->
    <div 
      v-for="(location, locationKey) in physicalAssessmentImages.anterior.locations"
      :key="locationKey"
      class="iv-image iv-image-body-location"
      :style="location.styles"
    >
      <img
        :src="location.image"
      >
    </div>
    <!-- Outer site images -->
    <div 
      v-for="(location, locationKey) in ivSites.locations"
      :key="'outer-' + locationKey"
      class="iv-image"
      :style="siteStylesOuter(location)"
    >
      <img
        :src="ivSites.outerImage"
        class="iv-image iv-image-site-outer"
        :class="{ disabled: readonly }"
      >
    </div>
    <!-- Inner site images -->
    <div 
      v-for="(location, locationKey) in ivSites.locations"
      :key="'inner-' + locationKey"
      class="iv-image"
      :style="siteStylesInner(location)"
    >
      <img
        :src="ivSites.innerImage"
        class="iv-image iv-image-site"
        :class="siteClasses(location.site, location.side)"
      >
    </div>
    <!-- IV stand -->
    <img
      :src="ivStandImages.stand.image"
      :style="ivStandImages.stand.styles"
      class="iv-image iv-image-stand"
      :class="{ disabled: readonly }"
    >
    <!-- IV bag connect -->
    <img
      :src="ivStandImages.bagConnect.image"
      :style="ivStandImages.bagConnect.styles"
      class="iv-image iv-image-bag-to-drip"
      :class="{ disabled: readonly, selected: ivEnabled }"
    >
    <!-- IV bag -->
    <img
      v-if="ivEnabled"
      :src="ivStandImages.bag.image"
      :style="ivStandImages.bag.styles"
      class="iv-image iv-image-bag selected"
      :class="{ disabled: readonly }"
    >
    <!-- Bag badge -->
    <md-badge
      v-if="ivEnabled"
      class="md-primary iv-image iv-image-badge" 
      :md-content="caseConf.iv.bagCount"
      :style="ivStandImages.badge.styles"
    />
    <!-- IV drip set -->
    <img
      v-if="ivEnabled"
      :src="ivStandImages.dripSet.image"
      :style="ivStandImages.dripSet.styles"
      class="iv-image iv-image-dripset"
      :class="{ disabled: readonly, selected: ivDripEnabled }"
    >
    <!-- IV drip connect -->
    <img
      :src="ivStandImages.dripConnect.image"
      :style="ivStandImages.dripConnect.styles"
      class="iv-image iv-image-drop-to-pump"
      :class="{ disabled: readonly, selected: ivEnabled }"
    >
    <!-- IV pump pad -->
    <img
      :src="ivStandImages.pumpControls.image"
      :style="ivStandImages.pumpControls.styles"
      class="iv-image iv-image-pad"
    >
    <!-- IV pump column -->
    <img
      :src="ivStandImages.pumpColumn.image"
      :style="ivStandImages.pumpColumn.styles"
      class="iv-image iv-image-pump"
      :class="{ disabled: readonly, selected: ivEnabled && ivPumpEnabled }"
    >
    <!-- IV pump connect -->
    <img
      :src="ivStandImages.pumpConnect.image"
      :style="ivStandImages.pumpConnect.styles"
      class="iv-image iv-image-pump-connect"
      :class="{ disabled: readonly, selected: ivEnabled }"
    >
  </div>
</template>

<script>
import get from 'lodash/get';

import avatars from '~lib/common/config/avatars';

import physicalAssessmentImagesMale from '@/config/physicalAssessmentImagesMale';
import physicalAssessmentImagesFemale from '@/config/physicalAssessmentImagesFemale';
import ivStandImages from '@/config/ivStandImages';
import ivSites from '@/config/ivSiteImages';

export default {
  name: 'iv-diagram',
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ivStandImages,
      ivSites
    };
  },
  computed: {
    genderType() {
      // Use avatar key to detect changes to avatar type.  A computed
      // property for the avatarOptions.genderType field won't work because
      // its too deeply nested for Vue change detection
      const avatarKey = get(this.caseConf, '_avatar.key', 'HAWTHORN');
      const avatar = avatars.find(a => a.key === avatarKey);
      if (avatar) {
        return avatar.avatarOptions.genderType;
      }
      return 'MALE';
    },
    physicalAssessmentImages() {
      if (this.genderType === 'FEMALE') {
        return physicalAssessmentImagesFemale;
      }
      return physicalAssessmentImagesMale;
    },
    allEnabledSites() {
      let sites = [];
      for (const type of this.caseConf.iv.types || []) {
        sites = sites.concat(type.sites);
      }
      return sites;
    },
    ivEnabled() {
      return this.caseConf.iv.enabled;
    },
    ivDripEnabled() {
      return this.caseConf.iv.dropSet.enabled;
    },
    ivPumpEnabled() {
      return this.caseConf.iv.pump.enabled;
    },
  },
  methods: {
    siteClasses(site, side) {
      if (!this.ivEnabled) {
        return;
      }
      let found = this.allEnabledSites.find(
        ivSite => ivSite.site === site && ivSite.sides.includes(side)
      );
      return {
        selected: found,
        disabled: this.readonly
      };
    },
    siteStylesOuter(location) {
      const coords = location.coords[this.genderType];
      if (!coords) {
        return; //tbd
      }
      return {
        left: coords.left + 'px',
        top: coords.top + 'px',
        width: '20px', //tbd
        zIndex: 2, //tbd
      };
    },
    siteStylesInner(location) {
      const coords = location.coords[this.genderType];
      if (!coords) {
        return; //tbd
      }
      return {
        left: coords.left + 2 + 'px',
        top: coords.top + 1.5 + 'px',
        width: '16px', //tbd
        zIndex: 3, //tbd
      };
    },
  },
}
</script>

<style scoped>
.physical-assessment-locations {
  position: relative;
  width: 307px;
  height: auto;
  transform: scale(0.9) translateY(60px);
}

.iv-image {
  position: absolute;
}

.iv-image.iv-image-body-location img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
}

.iv-image-site.selected,
.iv-image-bag-to-drip.selected,
.iv-image-bag.selected,
.iv-image-drop-to-pump.selected,
.iv-image-pump.selected,
.iv-image-pump-connect.selected,
.iv-image-dripset.selected {
  /* #0C70B0 */
  filter: brightness(0) saturate(100%) invert(30%) sepia(21%) saturate(6196%) hue-rotate(183deg) brightness(92%) contrast(91%);
}

.iv-image-site.selected.disabled,
.iv-image-bag.selected.disabled,
.iv-image-pump.selected.disabled,
.iv-image-dripset.selected.disabled {
  /* #0C70B0 */
  filter: invert(30%) sepia(21%) saturate(6196%) hue-rotate(183deg) brightness(92%) contrast(91%);
}

.iv-image-body-background.disabled,
.iv-image-site-outer.disabled,
.iv-image-stand.disabled,
.iv-image-bag.disabled {
  /* #DDDDDD */
  filter: invert(80%) sepia(71%) saturate(4%) hue-rotate(280deg) brightness(114%) contrast(73%);
}

</style>